import { Alert, Box } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import AllocationsFiltersAutocomplete from '@/components/features/AllocationsGroupList/AllocationsFiltersAutocomplete';
import BaseTable from '@/components/shared/Table/BaseTable';
import useBaseTable from '@/components/shared/Table/hooks/useBaseTable';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { useAllocationGroups } from '@/hooks/useAllocationsGroup';
import { useTableNavigation } from '@/hooks/useTableNavigation';
import { DEFAULT_PAGE_SIZE, useTablePaginationState } from '@/hooks/useTablePaginationState';
import type { components } from '@/shared/api/openapi';

type AllocationGroup = components['schemas']['GetAllocationGroupsEndpoint_AllocationGroupResponse'];

const columnHelper = createColumnHelper<AllocationGroup>();

function AllocationsGroupList() {
  const { organizationId } = useOrganizationId();

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const scrollTopParam = searchParams.get('scrollTop');
  const locationState = location.state as { returnSearchParams?: string } | undefined;
  const returnSearchParams = locationState?.returnSearchParams;
  const { navigateToDetail, restoreScrollPosition } = useTableNavigation({
    containerRef: tableContainerRef,
  });

  // Get pagination state from URL (single source of truth)
  const { pagination, apiPage, setPagination } = useTablePaginationState({
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  // Get filter state from URL (single source of truth)
  const filterString = searchParams.get('groups');
  const filterEmails = searchParams.get('emails');

  const groupNames = filterString
    ? filterString
        .split(',')
        .filter(Boolean)
        .map((name) => ({ name }))
    : null;

  const emails = filterEmails
    ? filterEmails
        .split(',')
        .filter(Boolean)
        .map((email) => ({ email }))
    : null;

  // Fetch data with current pagination from URL
  const { allocationsGroup, isLoading, error } = useAllocationGroups(apiPage, pagination.pageSize, 'name', groupNames, emails);

  const allocationsGroupList = allocationsGroup?.items ?? [];

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        size: 50,
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('isActive', {
        header: 'Status',
        size: 50,
        cell: (info) => {
          const status = info.getValue();

          if (status === undefined || status === null) {
            return '-';
          }

          return status ? 'Active' : 'Inactive';
        },
      }),
    ],
    [],
  );

  useEffect(() => {
    if (!isLoading && allocationsGroupList.length > 0) {
      restoreScrollPosition();
    }
  }, [isLoading, allocationsGroupList.length, restoreScrollPosition, scrollTopParam, returnSearchParams]);

  // Restore URL params from navigation state when coming back from detail page
  useEffect(() => {
    if (returnSearchParams) {
      const restoredParams = new URLSearchParams(returnSearchParams);
      const currentGroups = searchParams.get('groups');
      const currentEmails = searchParams.get('emails');
      const restoredGroups = restoredParams.get('groups');
      const restoredEmails = restoredParams.get('emails');

      if ((restoredGroups && restoredGroups !== currentGroups) || (restoredEmails && restoredEmails !== currentEmails)) {
        setSearchParams(restoredParams, { replace: true });
      }
    }
  }, [returnSearchParams, searchParams, setSearchParams]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, pageIndex: newPage }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPagination({ pageIndex: 0, pageSize: newPageSize });
    const params = new URLSearchParams(window.location.search);
    params.delete('scrollTop');
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  };

  const handleRowClick = (allocationGroupId: string, allocationGroup: unknown) => {
    navigateToDetail(`/allocations/manage/${allocationGroupId}`, {
      allocationGroup,
    });
  };

  const tableWithData = useBaseTable<AllocationGroup>({
    tableData: allocationsGroupList,
    columns,
    isRowSelectionEnabled: false,
    isColumnVisibilityEnabled: false,
    isColumnOrderEnabled: false,
    hasPagination: true,
    pagination,
    onPaginationChange: setPagination,
    paginationCount: allocationsGroup?.totalCount ?? 0,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [10, 50, 100],
  });

  if (error) {
    return (
      <Alert
        severity='error'
        sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', lineHeight: '20px' }}
        data-testid='error-loading-allocationsGroup'
      >
        Error loading Inventory Allocations: {error.message}
      </Alert>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
        <AllocationsFiltersAutocomplete organizationId={organizationId} />
      </Box>
      <BaseTable<AllocationGroup>
        {...tableWithData}
        testId='allocationsGroup-table'
        ariaLabel='Inventory Allocations list'
        containerRef={tableContainerRef}
        onRowClick={(_, row) => handleRowClick(row.original.id?.toString() ?? '', row.original)}
        loadingData={{
          loadingMessage: 'Loading Inventory Allocations…',
          isLoading: isLoading,
        }}
        noDataMessage='No Inventory Allocations found.'
      />
    </>
  );
}

export default AllocationsGroupList;
